// src/layout/Header.jsx
import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import teambridgeLogo from "../assets/TeamBridge-Logo.png";
import userAvatar from "../assets/avatar.png";
import RoleSwitcher from "../auth/RoleSwitcher";
import "./Layout.css";

const MIN_SEARCH_CHARS = 4;
const GLOBAL_TOAST_CONTAINER_ID = "global";

const cleanStr = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return "";
    const lower = s.toLowerCase();
    if (lower === "null" || lower === "undefined") return "";
    return s;
};

export function Header({
    publicMode = false,
    hideSearch = false,
    searchTerm,
    setSearchTerm,
    setSearchTrigger,
    menuCollapsed = false,
    onToggleMenu,
}) {
    const [inputValue, setInputValue] = useState(searchTerm || "");

    // MSAL
    const { instance, accounts } = useMsal();
    const account = accounts?.[0] || null;
    const claims = account?.idTokenClaims || {};
    //console.log("Header: account", account, "claims", claims);

    // Name (works even in public mode)
    const sessionName = cleanStr(sessionStorage.getItem("tb_name"));
    const resolvedName =
        sessionName ||
        cleanStr(account?.name) ||
        cleanStr(claims.name) ||
        cleanStr(claims.preferred_username) ||
        "Guest";

    // Role only when authenticated views
    // Role is now handled by <RoleSwitcher /> component

    useEffect(() => setInputValue(searchTerm || ""), [searchTerm]);

    const runSearch = () => {
        if (publicMode) return;
        const q = (inputValue || "").trim();
        if (!q) return;

        if (q.length < MIN_SEARCH_CHARS) {
            toast.warn("Please enter at least 4 characters to search", {
                toastId: "min4-global-header-search",
                containerId: GLOBAL_TOAST_CONTAINER_ID,
            });
            return;
        }
        setSearchTerm(q);
        setSearchTrigger();
    };

    return (
        <div className={`tb-header ${publicMode ? "tb-header-public" : ""}`}>
            <div className="tb-brand">
                {typeof onToggleMenu === "function" && (
                    <button
                        type="button"
                        className="tb-icon-btn tb-menu-btn"
                        onClick={onToggleMenu}
                        aria-label={menuCollapsed ? "Expand menu" : "Collapse menu"}
                        title={menuCollapsed ? "Expand menu" : "Collapse menu"}
                    >
                        <span aria-hidden="true">{menuCollapsed ? "☰" : "❮"}</span>
                    </button>
                )}

                <img
                    src={teambridgeLogo}
                    alt="TeamBridge"
                    className="tb-teambridge-logo"
                    draggable="false"
                />
            </div>

            {!publicMode && !hideSearch && (
                <div className="tb-header-search">
                    <input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="tb-search-h"
                        placeholder="Search (min 4 chars (Name/DTVID/ATTUID/SecurityID))"
                        aria-label="Search"
                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                    />
                    <button
                        type="button"
                        onClick={runSearch}
                        className="tb-search-ico-h"
                        aria-label="Search"
                        title="Search"
                    >
                        🔍
                    </button>
                </div>
            )}

            <div className="tb-user">
                <img src={userAvatar} alt="User Avatar" className="tb-avatar" draggable="false" />
                <div className="tb-user-meta">
                    <div className="tb-user-greet">Welcome,</div>
                    <div className="tb-user-name">{resolvedName}</div>

                    {!publicMode && (
                        <div className="tb-user-role">
                            <RoleSwitcher />
                        </div>
                    )}

                    {!publicMode && (
                        <button
                            type="button"
                            className="tb-logout-link"
                            onClick={() =>
                                instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
                            }
                        >
                            Logout
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
